import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { looksLikeFullAddress, parseAddress, type Customer } from '@fsm/shared';
import { api } from '../lib/api';
import { Button, Field, Input, Modal } from './ui';

/**
 * A new customer, typed while they are still on the phone.
 *
 * Two callers, one difference between them. From the customers page the
 * address is optional — somebody is tidying records, and half a record beats
 * none. From the middle of booking a job it is not, because a job needs an
 * address and offering to skip it only produces a customer that cannot yet be
 * booked, two screens from the person who wanted to book them.
 *
 * Nobody types an address they already have. Paste a whole one into the street
 * line and a button appears offering to split it across the fields — a button
 * rather than something automatic, because a form that rewrites itself while
 * you type is a form you stop trusting, and because the split is occasionally
 * wrong, which is fine when you asked for it and infuriating when you did not.
 */
export function NewCustomerModal({
  open,
  onClose,
  onCreated,
  requireAddress,
  defaultName,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (customer: Customer) => void;
  /**
   * True when the caller needs a bookable customer rather than a record. The
   * created customer is then guaranteed to come back with one property, which
   * is what lets the job form select it without a second round trip.
   */
  requireAddress?: boolean;
  /** Whatever was typed in the search box that found nobody. */
  defaultName?: string;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    line1: '',
    line2: '',
    city: '',
    state: 'TX',
    postalCode: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [split, setSplit] = useState<string | null>(null);

  /*
   * Carry the failed search across. Somebody who typed "Whitlock", found
   * nobody and pressed the button has already told us the name once.
   */
  useEffect(() => {
    if (!open) return;
    setForm((f) => ({ ...f, name: defaultName?.trim() || f.name }));
  }, [open, defaultName]);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (key === 'line1') setSplit(null);
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  function splitAddress() {
    const parsed = parseAddress(form.line1);
    setForm((f) => ({
      ...f,
      line1: parsed.line1,
      line2: parsed.line2 || f.line2,
      city: parsed.city || f.city,
      state: parsed.state || f.state,
      postalCode: parsed.postalCode || f.postalCode,
    }));
    setSplit(
      parsed.city
        ? 'Split into the fields below.'
        : 'Took the state and ZIP. The city could not be told apart from the street — add it yourself.',
    );
  }

  const create = useMutation({
    mutationFn: () =>
      api.customers.create({
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        property: form.line1.trim()
          ? {
              line1: form.line1.trim(),
              line2: form.line2.trim() || null,
              city: form.city.trim(),
              state: form.state.trim(),
              postalCode: form.postalCode.trim(),
              isPrimary: true,
            }
          : undefined,
      }),
    onSuccess: (customer) => {
      void qc.invalidateQueries({ queryKey: ['customers'] });
      reset();
      onCreated(customer);
    },
    onError: (e) => setError((e as { message?: string }).message ?? 'Could not save'),
  });

  function reset() {
    setForm({
      name: '',
      phone: '',
      email: '',
      line1: '',
      line2: '',
      city: '',
      state: 'TX',
      postalCode: '',
    });
    setError(null);
    setSplit(null);
  }

  function close() {
    reset();
    onClose();
  }

  /*
   * The address is all-or-nothing when it is optional: type into any part of
   * it and the rest becomes required, because half an address is worse than
   * none. When it is required it is simply required.
   */
  const addressStarted = Boolean(requireAddress) || form.line1.trim().length > 0;
  const addressComplete =
    form.line1.trim().length > 0 &&
    form.city.trim().length > 0 &&
    form.state.trim().length > 0 &&
    form.postalCode.trim().length > 0;

  const canSubmit = form.name.trim().length > 0 && (!addressStarted || addressComplete);

  return (
    <Modal
      open={open}
      onClose={close}
      title="New customer"
      description={
        requireAddress
          ? 'The address comes back on the job as the place to send somebody.'
          : 'The address is optional now, but a job needs one later.'
      }
      footer={
        <>
          <Button onClick={close}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            {requireAddress ? 'Add and use' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name" required>
          <Input value={form.name} onChange={set('name')} placeholder="Dana Whitlock" autoFocus />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone">
            <Input value={form.phone} onChange={set('phone')} placeholder="(512) 555-0142" />
          </Field>
          <Field label="Email" hint="where invoices go">
            <Input
              type="email"
              value={form.email}
              onChange={set('email')}
              placeholder="dana@example.com"
            />
          </Field>
        </div>

        <div className="border-t border-line pt-4">
          <Field label="Street address" required={Boolean(requireAddress)}>
            <Input
              value={form.line1}
              onChange={set('line1')}
              placeholder="412 Buckthorn Ln — or paste the whole address"
            />
          </Field>

          {looksLikeFullAddress(form.line1) && (
            <button
              type="button"
              onClick={splitAddress}
              className="mt-2 w-full rounded-lg border border-accent-line bg-accent-soft px-3 py-2 text-left text-[13px] font-semibold text-accent"
            >
              Split this into street, city, state and ZIP
            </button>
          )}
          {split && <p className="mt-2 text-[12px] text-ink-3">{split}</p>}

          <Field label="Unit or floor" hint="optional" className="mt-3">
            <Input value={form.line2} onChange={set('line2')} placeholder="Apt 4" />
          </Field>

          <div className="mt-3 grid grid-cols-[2fr_1fr_1fr] gap-3">
            <Field label="City" required={addressStarted}>
              <Input value={form.city} onChange={set('city')} />
            </Field>
            <Field label="State" required={addressStarted}>
              <Input value={form.state} onChange={set('state')} />
            </Field>
            <Field label="ZIP" required={addressStarted}>
              <Input value={form.postalCode} onChange={set('postalCode')} />
            </Field>
          </div>
        </div>

        {error && (
          <p className="rounded-lg bg-danger-soft px-3 py-2 text-[13px] text-danger">{error}</p>
        )}
      </div>
    </Modal>
  );
}
